import React, { useRef, useState } from "react";
import { Alert, Box, Button, SxProps, Theme, Typography } from "@mui/material";
import { CSVLink } from "react-csv";
import { bulkUploadUsers } from "../api/requests/users";
import { colors } from "../utils/constants";
import UploadFileIcon from "../assets/UploadFileIcon.svg";
import DownloadTemplateIcon from "../assets/DownloadTemplateIcon.svg";

const csvData = [
  [
    "FullName",
    "MobileNo",
    "EmployeeId",
    "Email",
    "PoliceStation",
    "ZoneId",
    "Location",
    "AssemblyConstituencyId",
    "DateOfBirth",
  ],
];

const BulkInvite = ({
  closeDialog,
}: {
  closeDialog: (buttonText: string) => void;
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvFileName, setCsvFileName] = useState<string>("");
  const [messageNote, setMessageNote] = useState<string>("");
  const [responseData, setResponseData] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCloseDialog = (buttonText: string) => {
    closeDialog(buttonText);
  };

  const uploadCSVFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        setMessageNote("Please upload a valid CSV file");
        return;
      }
      setCsvFile(file);
      setCsvFileName(file.name);
      setMessageNote("");
    }
  };

  const resetFileInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setCsvFile(null);
    setCsvFileName("");
    setMessageNote("");
  };

  const sendCsvFile = async () => {
    if (!csvFile) {
      setMessageNote("Please upload a CSV file");
      return;
    }
    setMessageNote("");
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      const response = await bulkUploadUsers({ formData });

      if (response && response.data) {
        resetFileInput();
        setResponseData(response.data);
      }
    } catch (error: any) {
      setMessageNote("Error uploading file.");
      console.error("Upload error:", error);
    }
  };

  if (responseData) {
    const totalRecords =
      (responseData.skipped ?? 0) + (responseData.totalUploaded ?? 0);
    const failedRecords = responseData.skipped ?? 0;
    const successfulRecords = responseData.totalUploaded ?? 0;
    const hasErrors =
      Array.isArray(responseData.errors) && responseData.errors.length > 0;

    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ color: "#000000" }}>
            Total records attempted: <span>{totalRecords}</span>
          </Typography>
          <Typography variant="body1" sx={{ color: "green" }}>
            Total records uploaded successfully:{" "}
            <span>{successfulRecords}</span>
          </Typography>
          <Typography variant="body1" sx={{ color: "red" }}>
            Total records failed: <span>{failedRecords}</span>
          </Typography>
        </Box>
        {responseData.isSuccess === false && hasErrors && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              Errors:
            </Typography>
            <ul
              style={{ paddingLeft: "0px", margin: 0, listStyleType: "none" }}
            >
              {responseData.errors.map((error: string, index: number) => (
                <li key={index}>
                  <Typography variant="body2">
                    {`${index + 1}. ${error}`}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}
        <Button
          variant="contained"
          onClick={() =>
            handleCloseDialog(
              totalRecords !== failedRecords ? "invite" : "close"
            )
          }
        >
          Close
        </Button>
      </Box>
    );
  }
  return (
    <Box component={"form"} sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ borderStyle: "dotted", borderColor: "gray", margin: 2 }}>
        <Box sx={bulkInviteMainStyles}>
          <Box sx={csvUploadStyles}>
            <img src={UploadFileIcon} />
            <Button
              component="label"
              sx={{ textTransform: "inherit", color: colors.indigoBlueColor }}
            >
              Select a CSV File to Upload
              <input
                ref={inputRef}
                hidden
                type="file"
                accept=".csv"
                onChange={uploadCSVFile}
              />
            </Button>
            <Box sx={resetButtonWrapper}>
              <Typography variant="body1">{csvFileName}</Typography>
              {csvFileName && (
                <Button onClick={resetFileInput} sx={resetButtonStyles}>
                  Reset
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {messageNote && (
        <Box sx={{ mt: 2 }}>
          <Alert
            sx={{ fontSize: 12 }}
            severity={
              messageNote.includes("successful") ? "success" : "warning"
            }
          >
            {messageNote}
          </Alert>
        </Box>
      )}
      <Box sx={actionButtonsStyles}>
        <CSVLink
          style={csvLinkStyles}
          data={csvData}
          filename={"user_data_template.csv"}
        >
          <img src={DownloadTemplateIcon} alt="download icon" />
          <Typography sx={{ pl: "6px", fontSize: "14px" }} variant="body1">
            Download Template
          </Typography>
        </CSVLink>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            alignItems: "center",
          }}
        >
          <Button
            size="small"
            onClick={() => handleCloseDialog("cancel")}
            sx={cancelButtonStyles}
          >
            Cancel
          </Button>
          <Button size="small" onClick={sendCsvFile} sx={inviteButtonStyles}>
            Invite
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkInvite;

const bulkInviteMainStyles: SxProps = {
  display: "flex",
  flexDirection: "column",
  backgroundColor: colors.aliceBlueColor,
};

const csvUploadStyles: SxProps = {
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
  pt: 3,
  pb: 1,
};

const actionButtonsStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  p: (theme) => theme.spacing(2, 2, 0, 2),
};

const csvLinkStyles = {
  color: "#999999",
  display: "flex",
  alignItems: "center",
  gap: "2px",
  alignSelf: "center",
};

const resetButtonWrapper: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: 2,
};

const resetButtonStyles: SxProps = {
  textTransform: "inherit",
  backgroundColor: colors.indigoBlueColor,
  color: colors.white,
  height: "25px",
  fontSize: "12px",
};

const cancelButtonStyles: SxProps = {
  border: `1px solid ${colors.indigoBlueColor}`,
  color: colors.indigoBlueColor,
  textTransform: "inherit",
  padding: "4px 16px",
  borderRadius: "6px",
};

const inviteButtonStyles: SxProps = {
  backgroundColor: colors.indigoBlueColor,
  color: colors.white,
  textTransform: "inherit",
  padding: "4px 20px",
  borderRadius: "6px",
};
